import { Hub, Storage } from 'aws-amplify';
import { useEffect, useState } from 'react';

export interface BucketImage {
	key: string;
	level?: 'public' | 'protected' | 'private';
	identityId?: string;
}

// Module-level cache to prevent blink on component remount.
// Signed URLs are valid for ~15 min by default, cache for 10 min.
// Cleared on sign-out to prevent cross-user URL leakage.
const urlCache = new Map<string, { url: string; expiry: number }>();
const CACHE_TTL = 10 * 60 * 1000;

Hub.listen('auth', ({ payload }) => {
	if (payload.event === 'signOut') {
		urlCache.clear();
	}
});

function getCacheKey(image: BucketImage): string {
	return `${image.level ?? 'public'}:${image.identityId ?? ''}:${image.key}`;
}

export const useBucketImage = (image?: BucketImage) => {
	const cacheKey = image ? getCacheKey(image) : '';
	const cached = cacheKey ? urlCache.get(cacheKey) : undefined;
	const initialSrc = cached && cached.expiry > Date.now() ? cached.url : undefined;

	const [src, setSrc] = useState<string | undefined>(initialSrc);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (image?.key) {
			const key = getCacheKey(image);
			const hit = urlCache.get(key);
			if (hit && hit.expiry > Date.now()) {
				setSrc(hit.url);
				setLoading(false);
				return;
			}
			setLoading(true);
			const { key: imgKey, level, identityId } = image;
			Storage.get(imgKey, {
				level,
				identityId
			}).then((url) => {
				urlCache.set(key, { url, expiry: Date.now() + CACHE_TTL });
				setSrc(url);
				setLoading(false);
			});
		} else {
			setSrc(undefined);
		}
	}, [image]);

	return { src, loading };
};
